import type { ServerDetail, ServerRankingSort, ServerTimeRow, ServerTopPlayer } from '@ottd/api';
import { useTranslation } from 'react-i18next';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { PageHeader } from '@/components/prototype/page-header';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { PodiumCard } from '@/components/prototype/podium-card';
import { RelativeTime } from '@/components/prototype/relative-time';
import { SegmentedTabs } from '@/components/prototype/segmented-tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { formatNumber, formatSignedPoints } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PlayerLink, RecordTimeLink } from '@/pages/shared';

/** Rows per ranking page (the API's page size). */
const ROWS_PER_PAGE = 100;

/** The rank column shrinks to its widest number ("#" heads it). */
const RANK_CELL = 'w-px whitespace-nowrap pr-0';

/** Podium order, left to right: runner-up, leader, third. */
const PODIUM_PLACES = [2, 1, 3];

interface ServerRankingProps {
  server: ServerDetail;
  page: number;
  /** The ranking the URL asks for - the tabs follow it. */
  sort: ServerRankingSort;
  onPageChange: (page: number) => void;
  onSortChange: (sort: ServerRankingSort) => void;
}

/**
 * The server's all-time ranking (legacy `/server/{id}` list), laid out like the
 * season ladder: podium + standings, 100 rows a page. By score (players, their
 * all-time points - the default) or by time (finished games, fastest first; one
 * player can hold several places).
 *
 * The body renders the ranking the payload carries: while a switched ranking
 * loads, the previous one stays up rather than a mismatched shape.
 */
export function ServerRanking({
  server,
  page,
  sort,
  onPageChange,
  onSortChange,
}: ServerRankingProps) {
  const { t } = useTranslation('server');
  const { t: tc } = useTranslation('common');
  const byTime = server.topPlayersSort === 'time';
  const count = byTime ? server.topTimes.length : server.topPlayers.length;
  const emptyLabel = page > 1 ? 'detail.emptyPage' : byTime ? 'detail.emptyTime' : 'detail.empty';

  return (
    <section aria-label={t('detail.topPlayersLabel')}>
      <PageHeader
        eyebrow={t('detail.eyebrow', { server: server.name })}
        title={t(byTime ? 'detail.titleTime' : 'detail.title', { top: page * ROWS_PER_PAGE })}
        subtitle={t(byTime ? 'detail.subtitleTime' : 'detail.subtitle')}
        className="mb-4"
      />
      <SegmentedTabs
        value={sort}
        onChange={onSortChange}
        label={t('detail.sortLabel')}
        className="mb-6"
        options={[
          { value: 'points', label: t('detail.sortScore') },
          { value: 'time', label: t('detail.sortTime') },
        ]}
      />

      {count === 0 ? (
        <p className="text-dim font-mono text-sm">{t(emptyLabel)}</p>
      ) : (
        <>
          {/* The podium is the ranking's top three - page 1 only. */}
          {page === 1 ? (
            byTime ? (
              <TimePodium rows={server.topTimes} />
            ) : (
              <ScorePodium rows={server.topPlayers} />
            )
          ) : null}

          <BevelWindow
            title={t('detail.standings')}
            action={tc('units.ranked', { count: server.topPlayersMeta.total })}
            bodyClassName="p-0"
          >
            {byTime ? (
              <TimeTable rows={server.topTimes} />
            ) : (
              <ScoreTable rows={server.topPlayers} />
            )}
          </BevelWindow>
        </>
      )}

      {/* Outside the empty branch so a page past the end offers a way back. */}
      <Pagination
        className="mt-6"
        page={page}
        lastPage={server.topPlayersMeta.last_page}
        onPageChange={onPageChange}
      />
    </section>
  );
}

function winPct(row: ServerTopPlayer): number {
  return row.games > 0 ? Math.round((row.wins / row.games) * 100) : 0;
}

/** The winner's label: their player, else the company (a game from before player attribution). */
function winnerName(row: ServerTimeRow): string {
  return row.player ?? row.company ?? '-';
}

function ScorePodium({ rows }: { rows: ServerTopPlayer[] }) {
  const { t } = useTranslation('server');

  return (
    <div className="mb-8 grid items-end gap-4 sm:grid-cols-3">
      {PODIUM_PLACES.map((place) => (
        <PodiumCard
          key={place}
          row={rows.find((row) => row.rank === place)}
          place={place}
          leaderTitle={t('detail.leaderTitle')}
          caption={t('detail.caption')}
        />
      ))}
    </div>
  );
}

function TimePodium({ rows }: { rows: ServerTimeRow[] }) {
  const { t } = useTranslation('server');

  return (
    <div className="mb-8 grid items-end gap-4 sm:grid-cols-3">
      {PODIUM_PLACES.map((place) => {
        const row = rows.find((entry) => entry.rank === place);

        return (
          <PodiumCard
            key={place}
            row={
              row
                ? {
                    name: winnerName(row),
                    avatar: row.avatar,
                    level: row.level,
                    linked: row.player !== null,
                  }
                : undefined
            }
            place={place}
            leaderTitle={t('detail.leaderTitleTime')}
            caption={t('detail.captionTime')}
            figure={row ? <RecordTimeLink time={row.time} gameId={row.gameId} /> : undefined}
          />
        );
      })}
    </div>
  );
}

function ScoreTable({ rows }: { rows: ServerTopPlayer[] }) {
  const { t } = useTranslation('server');
  const { t: tc } = useTranslation('common');

  return (
    <Table aria-label={t('detail.standingsLabel')}>
      <TableHeader>
        <TableRow>
          <TableHead className={RANK_CELL}>{t('columns.rank')}</TableHead>
          <TableHead>{t('columns.player')}</TableHead>
          <TableHead>{t('columns.level')}</TableHead>
          <TableHead>{t('columns.games')}</TableHead>
          <TableHead>{t('columns.wins')}</TableHead>
          <TableHead>{t('columns.winPct')}</TableHead>
          <TableHead>{t('columns.playTime')}</TableHead>
          <TableHead>{t('columns.points')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.name}>
            <TableCell className={cn('text-faint', RANK_CELL)}>{row.rank}</TableCell>
            <TableCell>
              <span className="flex items-center gap-2">
                <PlayerAvatar name={row.name} src={row.avatar} size={24} />
                <PlayerLink name={row.name} className="text-strong font-sans" />
              </span>
            </TableCell>
            <TableCell className="text-dim">{tc('units.level', { level: row.level })}</TableCell>
            <TableCell>{formatNumber(row.games)}</TableCell>
            <TableCell>{formatNumber(row.wins)}</TableCell>
            <TableCell className="text-faint">{winPct(row)}%</TableCell>
            <TableCell className="text-faint whitespace-nowrap">{row.playTimeHuman}</TableCell>
            <TableCell className="text-green">{formatSignedPoints(row.points)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TimeTable({ rows }: { rows: ServerTimeRow[] }) {
  const { t } = useTranslation('server');

  return (
    <Table aria-label={t('detail.standingsLabelTime')}>
      <TableHeader>
        <TableRow>
          <TableHead className={RANK_CELL}>{t('columns.rank')}</TableHead>
          <TableHead>{t('columns.player')}</TableHead>
          <TableHead>{t('columns.time')}</TableHead>
          <TableHead>{t('columns.when')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.gameId}>
            <TableCell className={cn('text-faint', RANK_CELL)}>{row.rank}</TableCell>
            <TableCell>
              <span className="flex items-center gap-2">
                <PlayerAvatar name={winnerName(row)} src={row.avatar} size={24} />
                {row.player !== null ? (
                  <PlayerLink name={row.player} className="text-strong font-sans" />
                ) : (
                  <span className="text-dim font-sans">{winnerName(row)}</span>
                )}
              </span>
            </TableCell>
            <TableCell>
              <RecordTimeLink
                time={row.time}
                gameId={row.gameId}
                className="text-gold whitespace-nowrap"
              />
            </TableCell>
            <TableCell className="text-faint whitespace-nowrap">
              <RelativeTime iso={row.at} label={row.when} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
